/* Daily Dons — site-header.js
   The sticky nav's three behaviours, ported from SiteNav.tsx /
   useScrolledPastTop / useStickyNavTheme / MobileMenuOverlay.tsx:

   1. Frosted-glass materialise once a 1px sentinel scrolls out of view.
   2. Light/dark colour scheme auto-detection: the bar goes light whenever a
      [data-nav-theme="dark"] section sits underneath it (or the page's
      resting surface is dark).
   3. Mobile menu: circle wipe + staggered links, scroll lock, Escape,
      auto-close at the 1280px desktop breakpoint. */
(function () {
  'use strict';

  function init(header) {
    var navWrap = header.querySelector('[data-nav-wrap]');
    var nav = header.querySelector('[data-nav]');
    if (!navWrap || !nav) return;

    var restingBg = navWrap.getAttribute('data-resting') || 'var(--surface-page)';
    var restingDark = navWrap.getAttribute('data-resting-dark') === 'true';
    var burger = header.querySelector('[data-nav-burger]');
    var circle = document.querySelector('[data-menu-circle]');
    var menu = document.querySelector('[data-mobile-menu]');

    var scrolled = false, darkBehind = false, menuOpen = false;

    function glass() {
      var onDark = restingDark || darkBehind;
      return {
        bg: onDark ? 'rgba(31,32,26,0.72)' : 'rgba(252,250,242,0.72)',
        solid: onDark ? 'var(--dd-eerie-black)' : 'var(--dd-floral-white)',
        shadow: onDark
          ? '0 8px 24px -16px rgba(0,0,0,0.5)'
          : '0 8px 24px -16px rgba(31,32,26,0.35)'
      };
    }

    function render() {
      var g = glass();
      var onDark = restingDark || darkBehind;
      var light = menuOpen || onDark;
      navWrap.dataset.scrolled = String(scrolled);
      navWrap.style.background = menuOpen ? 'transparent' : (scrolled ? g.bg : restingBg);
      navWrap.style.backdropFilter = (!menuOpen && scrolled) ? 'blur(20px) saturate(180%)' : 'none';
      navWrap.style.webkitBackdropFilter = navWrap.style.backdropFilter;
      navWrap.style.boxShadow = (!menuOpen && scrolled) ? g.shadow : 'none';
      navWrap.style.setProperty('--nav-glass-solid', g.solid);
      nav.classList.toggle('is-light', light);
      nav.classList.toggle('is-menu-open', menuOpen);
      if (circle) circle.classList.toggle('is-dark-page', onDark);
    }

    /* 1. Sentinel: bar materialises once actually stuck. */
    var sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;';
    navWrap.parentNode.insertBefore(sentinel, navWrap);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        scrolled = !entries[0].isIntersecting;
        render();
      }).observe(sentinel);
    } else {
      window.addEventListener('scroll', function () {
        scrolled = window.scrollY > 4; render();
      }, { passive: true });
    }

    /* 2. Dark-section detection under the bar. */
    var darkSections = [].slice.call(document.querySelectorAll('[data-nav-theme="dark"]'));
    if (darkSections.length) {
      function checkDark() {
        var navBottom = navWrap.getBoundingClientRect().bottom;
        darkBehind = darkSections.some(function (s) {
          var r = s.getBoundingClientRect();
          return r.top <= navBottom && r.bottom >= navBottom;
        });
        render();
      }
      window.addEventListener('scroll', checkDark, { passive: true });
      window.addEventListener('resize', checkDark);
      checkDark();
    }

    /* 3. Mobile menu. */
    function setMenu(open) {
      if (menuOpen === open) return;
      menuOpen = open;
      window.dispatchEvent(new CustomEvent('dailydon:mobile-menu', { detail: { open: open } }));
      if (burger) {
        burger.setAttribute('aria-expanded', String(open));
        burger.setAttribute('aria-label', open ? burger.getAttribute('data-label-close') : burger.getAttribute('data-label-open'));
      }
      if (circle) {
        circle.classList.toggle('is-open', open);
        circle.classList.toggle('is-closing', !open);
      }
      if (menu) {
        if (open) {
          /* Links start below the sticky nav's measured bottom edge. */
          var bottom = Math.round(navWrap.getBoundingClientRect().bottom);
          menu.style.paddingTop = Math.max(72, bottom) + 8 + 'px';
          menu.classList.remove('is-closing');
          menu.classList.add('is-open');
          menu.removeAttribute('inert');
          window.DDOverlays && window.DDOverlays.lockScroll();
        } else {
          menu.classList.add('is-closing');
          menu.classList.remove('is-open');
          menu.setAttribute('inert', '');
          window.DDOverlays && window.DDOverlays.unlockScroll();
          setTimeout(function () { menu.classList.remove('is-closing'); }, 460);
        }
      }
      render();
    }

    if (burger) burger.addEventListener('click', function () { setMenu(!menuOpen); });
    if (menu) {
      menu.addEventListener('click', function (e) {
        if (e.target.closest('a')) setMenu(false);
      });
      menu.setAttribute('inert', '');
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menuOpen) setMenu(false);
    });
    var mq = window.matchMedia('(min-width: 1280px)');
    var onMq = function () { if (mq.matches && menuOpen) setMenu(false); };
    if (mq.addEventListener) mq.addEventListener('change', onMq);

    render();
  }

  function boot(root) {
    (root || document).querySelectorAll('[data-site-header]:not([data-header-init])')
      .forEach(function (el) { el.setAttribute('data-header-init', '1'); init(el); });
  }

  document.addEventListener('DOMContentLoaded', function () { boot(document); });
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
